import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import type { Client } from 'api';
import {
  ClientSelectChevron,
  ClientSelectEmpty,
  ClientSelectItem,
  ClientSelectList,
  ClientSelectPopover,
  ClientSelectSearchBox,
  ClientSelectTrigger,
} from 'features/clients/clients.styles';
import React, { useMemo, useRef, useState } from 'react';

// ─── Component ────────────────────────────────────────────────────────────────

interface ClientSelectProps {
  clients: Client[];
  value: string;
  onChange: (value: string) => void;
}

export const ClientSelect: React.FC<ClientSelectProps> = ({ clients, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);

  const selected = clients.find((c) => c.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q ? clients.filter((c) => c.name.toLowerCase().includes(q)) : clients;
  }, [clients, search]);

  const handleSelect = (id: string) => {
    onChange(id);
    setOpen(false);
    setSearch('');
  };

  const triggerWidth = anchorRef.current?.offsetWidth ?? 0;

  return (
    <ClickAwayListener onClickAway={() => { setOpen(false); setSearch(''); }}>
      <div>
        <ClientSelectTrigger
          type="button"
          ref={anchorRef}
          data-open={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: selected ? 1 : 0.45 }}>
            {selected ? selected.name : 'Select a client'}
          </span>
          <ClientSelectChevron open={open}>
            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
          </ClientSelectChevron>
        </ClientSelectTrigger>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1400, width: triggerWidth || undefined }}
        >
          <ClientSelectPopover style={{ ['--trigger-width' as string]: `${triggerWidth}px` }}>
            <ClientSelectSearchBox
              autoFocus
              placeholder="Search clients…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <ClientSelectList>
              <ClientSelectItem selected={value === ''} onClick={() => handleSelect('')}>
                — No client —
              </ClientSelectItem>
              {filtered.map((c) => (
                <ClientSelectItem key={c.id} selected={value === c.id} onClick={() => handleSelect(c.id)}>
                  {c.name}
                </ClientSelectItem>
              ))}
              {filtered.length === 0 && <ClientSelectEmpty>No clients found</ClientSelectEmpty>}
            </ClientSelectList>
          </ClientSelectPopover>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};
